<script setup lang="ts">
import { ref, computed, onUnmounted } from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";

/**
 * Lecteur vocal compact et stylable (le <audio controls> natif ne se thème
 * pas) : bouton play/pause rond, barre de progression cliquable, durée.
 */

const props = defineProps<{
  /** URL du fichier audio (déjà résolue). */
  src: string;
  loading?: boolean;
}>();

defineEmits<{
  (e: "delete"): void;
}>();

const isPlaying = ref(false);
const progress = ref(0); // 0..1
const currentTime = ref(0);
const duration = ref(0);

let audio: HTMLAudioElement | null = null;

const ensureAudio = (): HTMLAudioElement => {
  if (audio) return audio;
  audio = new Audio(props.src);
  audio.onloadedmetadata = () => {
    duration.value = audio?.duration ?? 0;
  };
  audio.ontimeupdate = () => {
    if (!audio) return;
    currentTime.value = audio.currentTime;
    progress.value = audio.duration ? audio.currentTime / audio.duration : 0;
  };
  audio.onended = () => {
    isPlaying.value = false;
    progress.value = 0;
    currentTime.value = 0;
  };
  return audio;
};

const toggle = async () => {
  const el = ensureAudio();
  if (isPlaying.value) {
    el.pause();
    isPlaying.value = false;
  } else {
    await el.play();
    isPlaying.value = true;
  }
};

const seek = (event: MouseEvent) => {
  const el = ensureAudio();
  if (!el.duration) return;
  const bar = event.currentTarget as HTMLElement;
  const rect = bar.getBoundingClientRect();
  const ratio = Math.min(
    1,
    Math.max(0, (event.clientX - rect.left) / rect.width),
  );
  el.currentTime = ratio * el.duration;
  progress.value = ratio;
};

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const timeLabel = computed(() =>
  duration.value
    ? `${formatTime(currentTime.value)} / ${formatTime(duration.value)}`
    : formatTime(currentTime.value),
);

onUnmounted(() => {
  audio?.pause();
  audio = null;
});
</script>

<template>
  <div class="audio-player-item d-flex align-items-center gap-2 rounded">
    <span v-if="loading" class="spinner-border spinner-border-sm mx-2" />
    <template v-else>
      <button class="play-btn flex-shrink-0" @click="toggle">
        <PhosphorIcon
          :name="isPlaying ? 'pause' : 'play'"
          :size="14"
          weight="fill"
        />
      </button>

      <div class="progress-track flex-grow-1" role="slider" @click="seek">
        <div class="progress-fill" :style="{ width: `${progress * 100}%` }" />
      </div>

      <span class="time-label flex-shrink-0">{{ timeLabel }}</span>
    </template>

    <DRBtnIcon
      variant="flat"
      :size="24"
      phosphor-icon="trash"
      class="flex-shrink-0"
      @click="$emit('delete')"
    />
  </div>
</template>

<style lang="scss" scoped>
$voice-color: #8b5cf6;

.audio-player-item {
  background-color: color-mix(in srgb, #{$voice-color} 16%, transparent);
  border: 1px solid color-mix(in srgb, #{$voice-color} 35%, transparent);
  padding: 5px 8px;
}

.play-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background-color: $voice-color;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    filter: brightness(1.15);
  }
}

.progress-track {
  height: 5px;
  border-radius: 3px;
  background-color: color-mix(in srgb, #{$voice-color} 30%, transparent);
  cursor: pointer;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  background-color: $voice-color;
  transition: width 0.1s linear;
}

.time-label {
  font-size: 0.7rem;
  color: var(--bs-secondary-color);
  font-variant-numeric: tabular-nums;
}
</style>
